import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, Swords, Shield, Gavel, Flame, User, Anchor, Volume2, VolumeX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DialecticCore } from "@/components/DialecticCore";
import { ToolSpecs } from "@/components/ToolSpecs";
import { ToolCounters } from "@/components/ToolCounters";

// Caractéristiques techniques par outil (encadré doré uniforme).
const SPECS = {
  proces: {
    icons: [Swords, Shield, Gavel],
    fr: ["3 rôles", "Procureur · Défense", "Verdict"],
    en: ["3 roles", "Prosecutor · Defence", "Verdict"],
    es: ["3 roles", "Fiscal · Defensa", "Veredicto"],
    nl: ["3 rollen", "Aanklager · Verdediging", "Vonnis"],
  },
  doute: {
    icons: [Flame, Sparkles, Anchor],
    fr: ["1 flamme", "Doute socratique", "Purification"],
    en: ["1 flame", "Socratic doubt", "Purification"],
    es: ["1 llama", "Duda socrática", "Purificación"],
    nl: ["1 vlam", "Socratische twijfel", "Zuivering"],
  },
  verites: {
    icons: [User, Swords, Sparkles],
    fr: ["3 âges", "Vérités croisées", "Regards multiples"],
    en: ["3 ages", "Crossed truths", "Multiple views"],
    es: ["3 edades", "Verdades cruzadas", "Miradas múltiples"],
    nl: ["3 leeftijden", "Gekruiste waarheden", "Meerdere blikken"],
  },
  expedition: {
    icons: [Sparkles, User, Anchor],
    fr: ["3 formats", "Vidéo · Presse", "Podcast"],
    en: ["3 formats", "Video · Press", "Podcast"],
    es: ["3 formatos", "Vídeo · Prensa", "Podcast"],
    nl: ["3 formats", "Video · Pers", "Podcast"],
  },
};

const COLORS = {
  red: { card: "border-red-400/30 bg-red-400/[0.06]", chip: "bg-red-400/15 text-red-300", name: "text-red-300", label: "text-red-300/80" },
  teal: { card: "border-emerald-300/25 bg-emerald-300/[0.05]", chip: "bg-emerald-300/12 text-emerald-300", name: "text-emerald-300", label: "text-emerald-300/80" },
  gold: { card: "border-primary/25 bg-primary/[0.06]", chip: "bg-primary/15 text-primary", name: "text-primary", label: "text-primary/80" },
  amber: { card: "border-primary/25 bg-primary/[0.06]", chip: "bg-primary/15 text-primary", name: "text-primary", label: "text-primary/80" },
  sky: { card: "border-sky-300/20 bg-sky-300/[0.05]", chip: "bg-sky-300/12 text-sky-300", name: "text-sky-300/90", label: "text-sky-300/80" },
  grey: { card: "border-slate-400/30 bg-slate-400/[0.05]", chip: "bg-slate-400/15 text-slate-300", name: "text-slate-300", label: "text-slate-300/80" },
};

const ICONS = { swords: Swords, shield: Shield, gavel: Gavel, flame: Flame, user: User, anchor: Anchor };

// Son exclusif : activer le son d'une vidéo coupe toutes les autres.
const teaserSound = new Set();

// Teaser media: plays a looping muted cinematic that fades in; falls back to the
// poster image (and stays visible) if the browser blocks autoplay (e.g. iOS low-power).
function TeaserMedia({ video, image, testId }) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [edge, setEdge] = useState(1);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play?.().catch(() => setLoaded(true));
        else v.pause?.();
      },
      { threshold: 0.15, rootMargin: "200px 0px" }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const entry = { mute: () => { const v = ref.current; if (v) { v.muted = true; setMuted(true); } } };
    teaserSound.add(entry);
    return () => teaserSound.delete(entry);
  }, []);

  const toggleSound = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const v = ref.current;
    if (!v) return;
    const next = !v.muted;
    if (!next) teaserSound.forEach((entry) => entry.mute());
    v.muted = next;
    if (!next) { v.volume = 1; v.play?.().catch(() => {}); }
    setMuted(next);
  };

  // Fondu en boucle : la vidéo s'ouvre et se ferme en douceur à chaque boucle.
  const onTime = () => {
    const v = ref.current;
    if (!v || !v.duration || isNaN(v.duration)) return;
    const fd = 0.7;
    const cur = v.currentTime, d = v.duration;
    let o = 1;
    if (cur < fd) o = cur / fd;
    else if (cur > d - fd) o = Math.max(0, (d - cur) / fd);
    setEdge(Number(o.toFixed(2)));
  };

  if (!video) {
    return <img src={image} alt="" loading="lazy" className="w-full h-56 sm:h-72 object-cover" data-testid={`${testId}-image`} />;
  }
  return (
    <div className="relative">
      <video
        ref={ref}
        src={video}
        poster={image}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        onCanPlay={() => setLoaded(true)}
        onTimeUpdate={onTime}
        style={{ opacity: loaded ? edge : 0, transition: "opacity 0.25s linear" }}
        className="w-full h-56 sm:h-72 object-cover"
        data-testid={`${testId}-video`}
      />
      <button
        type="button"
        onClick={toggleSound}
        data-testid={`${testId}-sound-btn`}
        aria-label={muted ? "Activer le son" : "Couper le son"}
        className="absolute bottom-3 right-3 z-20 w-10 h-10 rounded-full bg-background/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
}

export function ComingSoonTeaser({ ns, Icon, image, video, personas, testId, navTab }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const lang = (i18n.language || "fr").slice(0, 2);
  const openTool = () => { if (!navTab) return; localStorage.setItem("coccon_pending_tab", navTab); navigate("/atelier"); };
  const tr = (k, opts) => t(`landing.${ns}.${k}`, opts);
  const texts = tr("personas", { returnObjects: true }) || [];
  const cols = personas.length >= 3 ? "sm:grid-cols-3" : personas.length === 2 ? "sm:grid-cols-2" : "";

  const concept = tr("concept", { returnObjects: true });
  const hasConcept = concept && typeof concept === "object" && concept.cBody;
  const cPersonas = Array.isArray(concept?.personas) ? concept.personas : [];
  const cCols = cPersonas.length >= 3 ? "sm:grid-cols-3" : cPersonas.length === 2 ? "sm:grid-cols-2" : "";
  const isTribunal = cPersonas.length === 3;
  const coreVariant = ns === "doute" ? "ember" : ns === "proces" ? "scale" : "tug";
  const payoffKind = ns === "doute" ? "ember" : "seal";

  return (
    <section className="max-w-6xl mx-auto px-6 pt-4 pb-14 sm:pb-20" data-testid={`${testId}-section`}>
      <motion.div
        initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        data-testid={`${testId}-teaser`}
        className="group relative block w-full text-left rounded-3xl overflow-hidden glass frame-pulse border border-primary/25 transition-all duration-500 will-change-transform hover:-translate-y-2 hover:shadow-[0_30px_64px_hsl(32_90%_50%/0.2)]"
        style={{ boxShadow: "0 0 40px hsl(32 90% 55% / 0.12)" }}
      >
        <div className="p-4 sm:p-6 pb-0">
          <div className="relative rounded-2xl overflow-hidden">
            <TeaserMedia video={video} image={image} testId={testId} />
            <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to top, hsl(222 32% 6% / 0.85), transparent 55%)" }} />
          </div>
        </div>
        <div className="p-8 sm:p-12 pt-6 sm:pt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <span className="w-16 h-16 rounded-2xl bg-primary/12 border border-primary/30 flex items-center justify-center text-primary flex-shrink-0 group-hover:scale-105 transition-transform">
            <Icon size={30} strokeWidth={1.6} />
          </span>
          <div className="flex-1">
            <span className="inline-block text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 border border-primary/25 rounded-full px-3 py-1 mb-3">
              {tr("badge")}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl text-gradient-orange leading-none mb-3">{tr("name")}</h2>
            <p className="text-foreground/60 leading-relaxed max-w-xl">{tr("tagline")}</p>
            {SPECS[ns] && (
              <ToolSpecs lang={lang} className="mt-5 w-full sm:max-w-sm"
                items={(SPECS[ns][lang] || SPECS[ns].fr).map((text, i) => ({ Icon: SPECS[ns].icons[i] || Sparkles, text }))} />
            )}
            <ToolCounters ns={ns} className="mt-4" />
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid={`${testId}-cta`}
            className="text-sm font-semibold text-primary/90 inline-flex items-center gap-2 whitespace-nowrap self-center border border-primary/30 rounded-full px-5 py-2.5 hover:bg-primary/10 transition-colors"
          >
            <Sparkles size={15} /> {tr("cta")}
          </button>
        </div>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        {hasConcept ? (
        <DialogContent className="glass border border-primary/25 max-w-2xl rounded-[22px] max-h-[88vh] overflow-y-auto scroll-thin" data-testid={`${testId}-modal`} style={{ boxShadow: "0 0 50px hsl(32 90% 55% / 0.15)" }}>
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-44 opacity-70" style={{ background: "radial-gradient(120% 100% at 50% 0%, hsl(32 90% 55% / 0.14), transparent 70%)" }} />
          <div className="relative z-10 grid gap-5">
            <DialogHeader className="space-y-2">
              <span className="inline-block w-fit text-[0.62rem] uppercase tracking-[0.28em] text-primary/70 border border-primary/25 rounded-full px-3 py-1" data-testid={`${testId}-eyebrow`}>{concept.eyebrow}</span>
              <DialogTitle className="font-display text-2xl sm:text-3xl text-gradient-orange flex items-center gap-2.5 pr-8 leading-tight">
                <Icon size={22} className="text-primary flex-shrink-0" /> {tr("name")}
              </DialogTitle>
            </DialogHeader>

            {/* Le Concept */}
            <section data-testid={`${testId}-concept-block`}>
              <p className="text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 mb-2">{concept.cLabel}</p>
              <DialogDescription className="text-foreground/70 leading-relaxed text-[0.95rem]">{concept.cBody}</DialogDescription>
            </section>

            {/* Personnages */}
            <section data-testid={`${testId}-personas-block`}>
              <p className="text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 mb-1.5">{concept.sectionLabel}</p>
              <p className="text-sm text-foreground/55 leading-relaxed mb-4">{concept.sectionIntro}</p>
              <div className={`grid gap-3 items-start ${cCols}`}>
                {cPersonas.map((p, i) => {
                  const col = COLORS[p.color] || COLORS.gold;
                  const PIcon = ICONS[p.icon] || Sparkles;
                  const elevated = isTribunal && i === 1;
                  return (
                    <div key={i} className={`rounded-2xl border p-4 ${col.card} ${elevated ? "sm:-translate-y-3 ring-1 ring-primary/25 shadow-[0_0_28px_rgba(245,158,11,0.14)]" : ""}`} data-testid={`${testId}-persona-${i}`}>
                      <p className={`font-display text-lg flex items-center gap-2.5 mb-1 ${col.name}`}>
                        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${col.chip}`}><PIcon size={17} /></span>
                        {p.name}
                      </p>
                      {p.tag && <span className={`inline-block text-[0.6rem] uppercase tracking-[0.22em] mb-2.5 ${col.label}`}>{p.tag}</span>}
                      {p.l1 && <p className="text-sm text-foreground/70 leading-relaxed mb-1.5"><span className={`font-medium ${col.label}`}>{p.l1} — </span>{p.v1}</p>}
                      {p.l2 && <p className="text-sm text-foreground/60 leading-relaxed"><span className={`font-medium ${col.label}`}>{p.l2} — </span>{p.v2}</p>}
                    </div>
                  );
                })}
              </div>
              <DialecticCore variant={coreVariant} label={concept.coreLabel} testId={`${testId}-core`} />
            </section>

            {/* Le dénouement (Verdict / Purification) */}
            {payoffKind === "seal" ? (
              <section className="relative rounded-2xl border border-primary/40 bg-primary/[0.05] p-5 overflow-hidden" data-testid={`${testId}-payoff-block`}>
                <div aria-hidden className="pointer-events-none absolute -right-6 -top-6 w-24 h-24 rounded-full border-2 border-primary/20 rotate-12 flex items-center justify-center">
                  <Gavel size={26} className="text-primary/30 -rotate-12" />
                </div>
                <p className="text-[0.7rem] uppercase tracking-[0.3em] text-primary/80 mb-2 flex items-center gap-1.5"><Gavel size={13} /> {concept.payoffLabel}</p>
                <p className="font-display italic text-foreground/85 leading-relaxed relative z-10">{concept.payoffBody}</p>
              </section>
            ) : (
              <section className="relative rounded-2xl border border-primary/30 p-5 overflow-hidden" data-testid={`${testId}-payoff-block`} style={{ background: "radial-gradient(120% 140% at 50% 120%, hsl(28 96% 40% / 0.22), transparent 70%)" }}>
                <p className="text-[0.7rem] uppercase tracking-[0.3em] text-primary/85 mb-2 flex items-center gap-1.5"><Flame size={13} /> {concept.payoffLabel}</p>
                <p className="font-display italic text-foreground/85 leading-relaxed">{concept.payoffBody}</p>
              </section>
            )}

            <div className="flex justify-center pt-1">
              <button onClick={openTool} data-testid={`${testId}-open`} className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                <Sparkles size={15} /> {tr("soon")}
              </button>
            </div>
          </div>
        </DialogContent>
        ) : (
        <DialogContent className="glass border border-primary/25 max-w-lg rounded-[22px] max-h-[88vh] overflow-y-auto scroll-thin" data-testid={`${testId}-modal`} style={{ boxShadow: "0 0 50px hsl(32 90% 55% / 0.15)" }}>
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-44 opacity-70" style={{ background: "radial-gradient(120% 100% at 50% 0%, hsl(32 90% 55% / 0.14), transparent 70%)" }} />
          <div className="relative z-10 grid gap-4">
            <DialogHeader>
              <DialogTitle className="font-display text-xl sm:text-2xl text-gradient-orange flex items-center gap-2 pr-8 leading-tight">
                <Icon size={20} className="text-primary flex-shrink-0" /> {tr("name")}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-foreground/70 leading-relaxed">{tr("modalIntro")}</DialogDescription>
            <div className={`grid gap-3 my-2 ${cols}`}>
              {personas.map((p, i) => {
                const c = COLORS[p.color] || COLORS.gold;
                const PIcon = p.Icon;
                return (
                  <div key={i} className={`rounded-2xl border p-4 ${c.card}`}>
                    <p className={`font-display text-lg flex items-center gap-2.5 mb-1.5 ${c.name}`}>
                      <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${c.chip}`}><PIcon size={17} /></span>
                      {texts[i]?.name}
                    </p>
                    <p className="text-sm text-foreground/60 leading-relaxed">{texts[i]?.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="font-display italic text-foreground/80 leading-relaxed">{tr("outro")}</p>
            <div className="flex justify-center mt-1">
              <button onClick={openTool} data-testid={`${testId}-open`} className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                <Sparkles size={15} /> {tr("soon")}
              </button>
            </div>
          </div>
        </DialogContent>
        )}
      </Dialog>
    </section>
  );
}
